import { createContext, useContext, useEffect, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode, useOneStoreMenuQuery } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useMinimalCart } from 'hooks/use-minimal-cart';
import { withSimpleCache } from 'state/graphql/with-simple-cache';
import { useServiceModeContext } from 'state/service-mode';
import { getDateInTimezone } from 'utils/dateTime';
import { mapLegacyToMinimalCartEntries } from 'utils/minimal-cart';
import { useMemoAll } from 'utils/use-memo-all';

import { findActiveDayPartIds } from './dayparts';
import { ILocalizedMenu, IMainMenuNode, IndexedMenu } from './types';
import { createIndexedMenu, createMainMenuNode, getPosServiceMode } from './utils';

export interface IUnifiedMenuContext {
  indexedMenu: IndexedMenu;
  mainMenu: IMainMenuNode[];
  isLoading: boolean;
}

export const UnifiedMenuContext = createContext<IUnifiedMenuContext>({
  indexedMenu: IndexedMenu.EMPTY,
  mainMenu: [],
  isLoading: false,
});

UnifiedMenuContext.displayName = 'UnifiedMenuContext';

export const useUnifiedMenuContext = (): IUnifiedMenuContext => {
  const context = useContext(UnifiedMenuContext);
  if (!context) {
    throw new Error('useUnifiedMenuContext must be used within a UnifiedMenuProvider');
  }
  return context;
};

const useCachedOneStoreMenuQuery = withSimpleCache(useOneStoreMenuQuery);

export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const isEnabled = useEnableUnifiedMenu();
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const { hydrateMinimalCart } = useMinimalCart();

  // Remove cartEntries if those are not compatible with the new menu
  const { cartEntries } = useCart();
  useEffect(() => {
    if (isEnabled) {
      const incompatibleEntries = cartEntries.filter(cartEntry => !cartEntry.posIdsConfiguration);
      if (incompatibleEntries.length > 0) {
        hydrateMinimalCart(
          mapLegacyToMinimalCartEntries(incompatibleEntries).map(entry => ({
            ...entry,
            isEdit: true,
          }))
        );
      }
    }
  }, [cartEntries, isEnabled, hydrateMinimalCart]);

  const { data, loading: isLoading } = useCachedOneStoreMenuQuery({
    variables: store?.number ? { storeId: store.number } : undefined,
    skip: !isEnabled || !store?.number,
  });

  const menu: ILocalizedMenu | undefined = data?.OneStoreMenu ?? undefined;

  const indexedMenu = useMemo(() => (menu ? createIndexedMenu(menu) : IndexedMenu.EMPTY), [menu]);
  const mainMenu = useMemo(() => maybeCreateMainMenu(menu, indexedMenu, serviceMode, store), [
    indexedMenu,
    menu,
    serviceMode,
    store,
  ]);

  const value: IUnifiedMenuContext = useMemoAll({
    mainMenu: mainMenu?.children ?? [],
    indexedMenu,
    isLoading,
  });

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu | undefined,
  serviceMode: ServiceMode | null,
  store: IRestaurant | undefined
): IMainMenuNode | undefined {
  if (indexedMenu && menu && serviceMode && store) {
    const now = store.timezone ? getDateInTimezone(store.timezone) : new Date();
    const activeDayPartIds = findActiveDayPartIds(menu.dayParts, now);
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode: getPosServiceMode(serviceMode),
      dayPartIds: activeDayPartIds,
    });
  }
  return undefined;
}
